import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded"
import { IconButton, Stack, Typography } from "@mui/material"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"
import { ReactComponent as NoScheme } from "../../assets/No_offers_ui.svg"
import useAppConfig from "../../hooks/useAppConfig"
import useAuth from "../../hooks/useAuth"
import Footer from "../../Layout/Footer"
import ROUTE_PATHS from "../../routes/routesPath"
import IfEmpty from "../../shared/constants/IfEmpty"
import STRING_CONSTANTS from "../../shared/constants/StringConstants"
import { Loader } from "../../shared/components/UI"
import {
  createSchemePayment,
  getSchemeSubscription,
} from "../../service/scheme"
import { SchemeContainer } from "../../styles/styledComponent"
import {
  PAYMENT_NOTICE_DURATION_MS,
  PAYMENT_POLL_ATTEMPTS,
  PAYMENT_POLL_INTERVAL_MS,
  PAYMENT_STATUS,
  PAYMENT_TOAST_ID,
  REFUND_NOTICE_DURATION_MS,
} from "./constants"
import {
  getPayErrorMessage,
  getPaymentOutcomeMessage,
  isInstalmentDue,
  mergePaymentScheme,
} from "./payment"
import { getSchemeMetrics } from "./selectors"
import SchemeInstalments from "./SchemeInstalments"
import SchemeSummaryCard from "./SchemeSummaryCard"
import useRazorpayCheckout from "./useRazorpayCheckout"
import useSchemeBanners from "./useSchemeBanners"
import useSchemeBusiness from "./useSchemeBusiness"

const SchemeDetail = () => {
  const { schemeId } = useParams()
  const navigate = useNavigate()
  const pwaConfig = useAppConfig()
  const { token } = useAuth()
  const business = useSchemeBusiness()
  const { footerImg } = useSchemeBanners(pwaConfig)
  const openCheckout = useRazorpayCheckout()

  const [scheme, setScheme] = useState(null)
  const [loading, setLoading] = useState(true)
  const [paying, setPaying] = useState(false)
  const pollTimer = useRef(null)
  const initialBusiness = useRef(business)

  const refetch = useCallback(
    ({ silent = false } = {}) => {
      if (!token || !schemeId) return Promise.resolve(null)
      if (!silent) setLoading(true)
      return getSchemeSubscription({ token, business, schemeId })
        .then((data) => {
          setScheme(data)
          return data
        })
        .catch(() => {
          if (!silent) setScheme(null)
          return null
        })
        .finally(() => {
          if (!silent) setLoading(false)
        })
    },
    [token, business, schemeId]
  )

  useEffect(() => {
    if (business !== initialBusiness.current) navigate(ROUTE_PATHS.SCHEME)
  }, [business, navigate])

  useEffect(() => {
    refetch()
  }, [refetch])

  useEffect(() => () => clearTimeout(pollTimer.current), [])

  const metrics = useMemo(
    () => (scheme ? getSchemeMetrics(scheme) : null),
    [scheme]
  )

  const pollWhileProcessing = (installmentNo, attempt = 1) => {
    clearTimeout(pollTimer.current)
    pollTimer.current = setTimeout(
      () =>
        refetch({ silent: true }).then((latest) => {
          if (
            attempt < PAYMENT_POLL_ATTEMPTS &&
            (!latest || isInstalmentDue(latest, installmentNo))
          ) {
            pollWhileProcessing(installmentNo, attempt + 1)
          }
        }),
      PAYMENT_POLL_INTERVAL_MS
    )
  }

  const applyPaymentOutcome = (response, installmentNo) => {
    const message = getPaymentOutcomeMessage(response)
    switch (response?.status) {
      case PAYMENT_STATUS.PAID:
        toast.success(message, { id: PAYMENT_TOAST_ID })
        break
      case PAYMENT_STATUS.PROCESSING:
        toast(message, {
          id: PAYMENT_TOAST_ID,
          duration: PAYMENT_NOTICE_DURATION_MS,
        })
        pollWhileProcessing(installmentNo)
        return
      case PAYMENT_STATUS.REFUNDED:
      case PAYMENT_STATUS.REFUND_FAILED:
        toast.error(message, {
          id: PAYMENT_TOAST_ID,
          duration: REFUND_NOTICE_DURATION_MS,
        })
        break
      default:
        toast.dismiss(PAYMENT_TOAST_ID)
        break
    }
    if (response?.scheme)
      setScheme((prev) => mergePaymentScheme(prev, response.scheme))
    else refetch({ silent: true })
  }

  const onPayFailed = (fallback) => (error) => {
    toast.error(getPayErrorMessage(error, fallback), { id: PAYMENT_TOAST_ID })
    refetch({ silent: true })
    setPaying(false)
  }

  const onPay = () => {
    if (paying) return
    const installmentNo = metrics?.currentDue?.installmentNo
    const onOutcome = (response) => {
      applyPaymentOutcome(response, installmentNo)
      setPaying(false)
    }
    setPaying(true)
    createSchemePayment({ token, business, schemeId }).then(
      (response) =>
        response?.status === PAYMENT_STATUS.CHECKOUT
          ? openCheckout(response.checkout, {
              onVerified: onOutcome,
              onError: onPayFailed(STRING_CONSTANTS.PAYMENT_CONFIRM_FAILED),
              onClose: () => {
                refetch({ silent: true })
                setPaying(false)
              },
              onPending: () => pollWhileProcessing(installmentNo),
            }).catch(onPayFailed(STRING_CONSTANTS.PAYMENT_WINDOW_FAILED))
          : onOutcome(response),
      onPayFailed()
    )
  }

  if (loading) return <Loader />

  return (
    <SchemeContainer>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <IconButton
          aria-label={STRING_CONSTANTS.BACK}
          onClick={() => navigate(ROUTE_PATHS.SCHEME)}
          sx={{ ml: -1 }}
        >
          <ArrowBackIosNewRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Typography component="h1" sx={{ fontSize: 18, fontWeight: 700 }}>
          {scheme?.schemeMaster?.name || STRING_CONSTANTS.SCHEME}
        </Typography>
      </Stack>

      {scheme && metrics ? (
        <>
          <SchemeSummaryCard scheme={scheme} metrics={metrics} />
          <SchemeInstalments
            scheme={scheme}
            metrics={metrics}
            paying={paying}
            onPay={onPay}
          />
        </>
      ) : (
        <IfEmpty
          Icon={NoScheme}
          head={STRING_CONSTANTS.EMPTY}
          desc={STRING_CONSTANTS.SCHEME_NOT_FOUND}
        />
      )}

      <Footer img={footerImg} />
    </SchemeContainer>
  )
}

export default SchemeDetail
